import { Suspense, lazy, useCallback, useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Spinner } from '@/components/ui/Spinner';
import { AppShell } from '@/components/layout/AppShell';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { Tour } from '@/components/Tour';
import { AIChatWidget } from '@/components/AIChatWidget';
import { useTour } from '@/contexts/TourContext';
import { useLocation, useNavigate } from 'react-router-dom';
import { api } from '@/lib/api';
const DashboardPanel = lazy(() => import('@/pages/panels/DashboardPanel').then(m => ({ default: m.DashboardPanel })));
const NewsPanel = lazy(() => import('@/pages/panels/NewsPanel').then(m => ({ default: m.NewsPanel })));
const BookmarksPanel = lazy(() => import('@/pages/panels/BookmarksPanel').then(m => ({ default: m.BookmarksPanel })));
const CalendarPanel = lazy(() => import('@/pages/panels/CalendarPanel').then(m => ({ default: m.CalendarPanel })));
const BacktestPanel = lazy(() => import('@/pages/panels/BacktestPanel').then(m => ({ default: m.BacktestPanel })));
const JournalPanel = lazy(() => import('@/pages/panels/JournalPanel').then(m => ({ default: m.JournalPanel })));
const EconomyPanel = lazy(() => import('@/pages/panels/EconomyPanel').then(m => ({ default: m.EconomyPanel })));
const CotPanel = lazy(() => import('@/pages/panels/CotPanel').then(m => ({ default: m.CotPanel })));
const ResearchPanel = lazy(() => import('@/pages/panels/ResearchPanel').then(m => ({ default: m.ResearchPanel })));
const LotCalcPanel = lazy(() => import('@/pages/panels/LotCalcPanel').then(m => ({ default: m.LotCalcPanel })));
const TutorialsPanel = lazy(() => import('@/pages/panels/TutorialsPanel').then(m => ({ default: m.TutorialsPanel })));
const PlansPanel = lazy(() => import('@/pages/panels/PlansPanel').then(m => ({ default: m.PlansPanel })));
const UpdatesPanel = lazy(() => import('@/pages/panels/UpdatesPanel').then(m => ({ default: m.UpdatesPanel })));
const AdminPanel = lazy(() => import('@/pages/panels/AdminPanel').then(m => ({ default: m.AdminPanel })));
const AffiliatePanel = lazy(() => import('@/pages/panels/AffiliatePanel').then(m => ({ default: m.AffiliatePanel })));

type PanelKey =
  | 'dashboard' | 'news' | 'bookmarks' | 'calendar' | 'backtest' | 'journal' | 'economy'
  | 'cot' | 'research' | 'lotcalc' | 'tutorials'
  | 'affiliate' | 'plans' | 'updates' | 'admin';

const panels: Record<PanelKey, React.FC> = {
  dashboard: DashboardPanel,
  news:      NewsPanel,
  bookmarks: BookmarksPanel,
  calendar:  CalendarPanel,
  backtest:  BacktestPanel,
  journal:   JournalPanel,
  economy:   EconomyPanel,
  cot:       CotPanel,
  research:  ResearchPanel,
  lotcalc:   LotCalcPanel,
  tutorials: TutorialsPanel,
  affiliate: AffiliatePanel,
  plans:     PlansPanel,
  updates:   UpdatesPanel,
  admin:     AdminPanel,
};

export function DashboardRoute() {
  // Persist across reloads so a chunk-load auto-reload keeps the user on the
  // panel they were viewing — otherwise every transient 404 dumped them back
  // to the dashboard, which read as "I clicked X, got bounced, clicked again
  // and now it worked". sessionStorage scopes per-tab.
  const [active, setActive] = useState<PanelKey>(() => {
    try {
      const stored = sessionStorage.getItem('telos:active-panel');
      if (stored && stored in panels) return stored as PanelKey;
    } catch { /* private mode etc — fall back to default */ }
    return 'dashboard';
  });
  useEffect(() => {
    try { sessionStorage.setItem('telos:active-panel', active); } catch { /* ignore */ }
  }, [active]);
  const Panel = panels[active];
  const { isActive: tourActive, stop: stopTour } = useTour();

  // Fallback for the case where Whop's webhook didn't arrive (mis-config,
  // network blip, etc.). When we land on /dashboard?payment=success, hit
  // /api/payments/sync to look up the user's active Whop membership by
  // email and apply the right plan — then reload so the gated UI unlocks.
  const location = useLocation();
  const navigate = useNavigate();
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('payment') !== 'success') return;
    // Strip the query param so a refresh doesn't re-fire the sync.
    const url = new URL(window.location.href);
    url.searchParams.delete('payment');
    navigate(url.pathname + url.search + url.hash, { replace: true });
    // Fire the sync. On success, reload so AuthContext re-fetches the user
    // and the paywall comes down.
    api.post('/api/payments/sync', {})
      .then((res) => {
        const r = res as { updated?: boolean; reason?: string; plan?: string };
        if (r?.updated) {
          // eslint-disable-next-line no-console
          console.log('[Payment sync] confirmed subscription:', r.plan);
          // User just paid via Whop — first time they reach the platform,
          // route them through /onboarding (pair-picker) before the live
          // dashboard. Onboarding submit lands on /dashboard where the
          // product tour auto-fires from the fresh-signup flag.
          navigate('/onboarding', { replace: true });
        } else {
          // eslint-disable-next-line no-console
          console.warn('[Payment sync] no matching subscription found:', r?.reason);
          // Stay on whatever the user is — the paywall will hold them if
          // they're free. If the webhook arrives later it will flip them.
        }
      })
      .catch((err) => {
        // eslint-disable-next-line no-console
        console.error('[Payment sync] request failed:', err);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  // Tour steps can ask us to switch to a specific panel before highlighting.
  // We only respond to keys we recognise; anything else is silently ignored.
  const handleTourPanelChange = useCallback((key: string) => {
    if (key in panels) setActive(key as PanelKey);
  }, []);

  // Cross-panel navigation via custom event (e.g. calendar → news).
  useEffect(() => {
    const onNavigate = (e: Event) => {
      const detail = (e as CustomEvent<string>).detail;
      if (detail && detail in panels) setActive(detail as PanelKey);
    };
    window.addEventListener('telos:navigate-panel', onNavigate as EventListener);
    return () => window.removeEventListener('telos:navigate-panel', onNavigate as EventListener);
  }, []);

  return (
    <AppShell activePanel={active} onPanelChange={(k) => setActive(k as PanelKey)}>
      <AnimatePresence mode="wait">
        <motion.div
          key={active}
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -8 }}
          transition={{ duration: 0.22, ease: [0.16, 1, 0.3, 1] }}
        >
          <ErrorBoundary section={active} key={`${active}-eb`}>
            <Suspense fallback={<div className="flex items-center justify-center py-20"><Spinner className="size-6" /></div>}>
              <Panel />
            </Suspense>
          </ErrorBoundary>
        </motion.div>
      </AnimatePresence>

      <Tour
        active={tourActive}
        onClose={stopTour}
        onPanelChange={handleTourPanelChange}
      />

      {/* Floating AI chat — persists across panel switches; only mounted on
          authenticated, paid dashboard route (auth/landing/onboarding/trial-
          expired pages don't reach this component). */}
      <AIChatWidget />
    </AppShell>
  );
}
